<template>
  <div>
    <v-row>
      <v-col cols="12" class="pb-0" v-if="!disabledForm">
        <v-combobox
          v-model="selectedItem"
          :hint="$t('forms.hint.peopleList')"
          :items="items"
          :search-input.sync="search"
          item-value="id"
          item-text="name"
          return-object
          persistent-hint
          :disabled="disabledForm"
          @change="changeCombobox"
          @input="addPersonCharge(selectedItem)"
          outlined
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("forms.comboboxPeople.nodata") }} "<strong>{{
                    search
                  }}</strong
                  >". {{ $t("forms.comboboxPeople.nodata_1") }}
                  <kbd>enter</kbd> {{ $t("forms.comboboxPeople.nodata_2") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </v-col>
      <v-col cols="12" sm="6" class="py-0">
        <v-list
          :class="changeColor ? 'border-box amber' : 'border-box'"
          v-if="itemsSelected !== undefined && itemsSelected.length"
          class="pt-0 selected-people-list"
        >
          <template v-slot:default>
            <v-list-item
              v-for="(item, index) in itemsSelected"
              :key="index"
              class="selected-person-item"
            >
              <v-list-item-icon class="selected-person-icon">
                <v-icon>mdi-account</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="selected-person-name">
                {{ getName(item) }}
              </v-list-item-title>
              <v-list-item-action class="selected-person-action">
                <v-btn 
                  color="error" 
                  :disabled="disabledForm"
                  icon
                  :aria-label="$t('button.delete')"
                  :title="$t('button.delete')"
                  @click="remove(item)">
                  <v-icon>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    itemsSelected: Array,
    disabledForm: Boolean,
    changeColor: Boolean,
    endpoint: {
      type: String,
      default: "personName"
    }
  },
  name: "selectPeopleComboBox",

  data: () => ({
    temporalIdCounter: -1,
    items: [],
    selectedItem: "",
    search: ""
  }),
  watch: {
    itemsSelected: {
      handler() {
        this.normalizeSelectedItems();
      },
      deep: true
    }
  },
  created() {
    this.initialize();
  },

  methods: {
    replaceSelectedItems(items) {
      if (!Array.isArray(this.itemsSelected)) {
        return;
      }
      this.itemsSelected.splice(0, this.itemsSelected.length, ...items);
    },
    getItemNameById(id) {
      const person = this.items.find(item => item.id === id);
      return person ? person.name : "";
    },
    normalizeSelectedItem(item) {
      if (item == null) {
        return null;
      }
      if (typeof item === "number") {
        return {
          id: item,
          name: this.getItemNameById(item)
        };
      }
      if (item.temporalId !== undefined) {
        return {
          id: item.id !== undefined ? item.id : null,
          name:
            item.name ||
            (item.id !== undefined && item.id !== null
              ? this.getItemNameById(item.id)
              : ""),
          temporalId: item.temporalId
        };
      }
      if (this.endpoint === "personName" && item.preferred_name !== undefined) {
        return {
          id: item.id,
          name:
            item.preferred_name.given_name +
            " " +
            item.preferred_name.family_name
        };
      }
      if (this.endpoint === "funderProgram" && item.internal_name !== undefined) {
        return {
          id: item.id,
          name: item.internal_name
        };
      }
      return {
        id: item.id !== undefined ? item.id : null,
        name:
          item.name ||
          (item.id !== undefined && item.id !== null
            ? this.getItemNameById(item.id)
            : "")
      };
    },
    normalizeSelectedItems() {
      if (!Array.isArray(this.itemsSelected) || this.itemsSelected.length === 0) {
        return;
      }

      const normalized = this.itemsSelected
        .map(item => this.normalizeSelectedItem(item))
        .filter(Boolean);

      const changed =
        normalized.length !== this.itemsSelected.length ||
        normalized.some((item, index) => {
          const current = this.itemsSelected[index];
          if (current === item) {
            return false;
          }
          if (typeof current === "number") {
            return true;
          }
          return (
            !current ||
            current.id !== item.id ||
            current.name !== item.name ||
            current.temporalId !== item.temporalId
          );
        });

      if (changed) {
        this.replaceSelectedItems(normalized);
      }
    },
    getName(item) {
      if (item == null) {
        return "";
      }
      if (item.temporalId !== undefined) {
        return item.name;
      }
      if (typeof item === "number") {
        return this.getItemNameById(item);
      }
      if (this.endpoint === "personName") {
        if (item.preferred_name !== undefined) {
          return (
            item.preferred_name.given_name +
            " " +
            item.preferred_name.family_name
          );
        } else {
          return item.name;
        }
      }
      if (this.endpoint === "funderProgram") {
        if (item.internal_name !== undefined) {
          return item.internal_name;
        } else {
          return item.name;
        }
      }
      return item.name;
    },
    getNewTemporalId() {
      let temporalIdCounter = -1;
      this.itemsSelected.forEach(item => {
        if (
          item.temporalId !== undefined &&
          item.temporalId <= temporalIdCounter
        ) {
          temporalIdCounter = item.temporalId - 1;
        }
      });
      return temporalIdCounter;
    },
    initialize() {
      const path = this.$store.state.endpoint + "/" + this.endpoint;
      axios
        .get(path)
        .then(response => {
          response.data.forEach(item => {
            if (this.endpoint === "personName") {
              this.items.push({
                id: item.id,
                name:
                  item.preferred_name.given_name +
                  " " +
                  item.preferred_name.family_name
              });
            } else if (this.endpoint === "funderProgram") {
              this.items.push({
                id: item.id,
                name: item.internal_name
              });
            } else{
              this.items.push({
                id: item.id,
                name: item.name
              });
            }
          });
          this.normalizeSelectedItems();
        })
        .catch(error => {
          console.log(error);
        });
    },
    addPersonCharge(add_item) {
      let kk = true;
      if (typeof add_item === "string") {
        this.items.forEach(item => {
          if (add_item === item.name) {
            add_item = { id: item.id, name: add_item };
            kk = false;
          }
        });
        if (kk) {
          add_item = {
            id: null,
            name: add_item,
            temporalId: this.getNewTemporalId()
          };
        }
      }
      kk = true;
      this.itemsSelected.forEach(item => {
        if (
          (add_item.id !== null && item.id === add_item.id) ||
          (add_item.id == null && item.name === add_item.name)
        ) {
          kk = false;
        }
      });
      if (kk && add_item.name !== "") {
        this.itemsSelected.push(add_item);
        this.temporalIdCounter--;
      }
      this.$nextTick(() => {
        this.selectedItem = null;
        this.search = "";
      });
    },
    remove(obj) {
      const list = [];
      this.itemsSelected.forEach(item => {
        if (item.id != null && item.id !== obj.id) {
          list.push(item);
        } else if (item.id == null && item.temporalId !== obj.temporalId) {
          list.push(item);
        }
      });

      this.itemsSelected.length = 0;
      list.forEach(item => {
        this.itemsSelected.push(item);
      });
      if (this.itemsSelected.length === 0) {
        this.itemsSelected.pop();
      }
      this.$emit("change");
    },
    changeCombobox() {
      this.search = "";
      this.$emit("change");
    }
  }
};
</script>

<style scoped>
.selected-people-list .selected-person-item {
  padding-left: 8px;
  padding-right: 4px;
}

.selected-person-icon {
  align-self: center;
  justify-content: center;
  margin: 0 8px 0 0;
  min-width: 24px;
}

.selected-person-name {
  min-width: 0;
  overflow: visible;
  text-overflow: clip;
  white-space: normal;
  word-break: break-word;
}

.selected-person-action {
  align-self: center;
  margin: 0 0 0 8px;
  min-width: 36px;
}

@media (max-width: 600px) {
  .selected-person-item {
    align-items: flex-start;
  }

  .selected-person-icon,
  .selected-person-action {
    margin-top: 6px;
  }
}
</style>
